import React, { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import "./content.css";
import Icon from "../ui/Icon";
import BrandIcon from "../ui/BrandIcon";
import { profile } from "../../data/site";

const {
  REACT_APP_EMAILJS_SERVICE_ID: SERVICE,
  REACT_APP_EMAILJS_TEMPLATE_ID: TEMPLATE,
  REACT_APP_EMAILJS_PUBLIC_KEY: KEY,
} = process.env;
const FORM_ENABLED = Boolean(SERVICE && TEMPLATE && KEY);

/* Client-side throttling. It stops accidental double sends and casual spam, but it cannot stop
   someone calling EmailJS directly: the real limits live in the EmailJS dashboard (see below). */
const COOLDOWN_MS = 60 * 1000;
const MAX_PER_HOUR = 3;
const MIN_FILL_MS = 3000;
const STORE_KEY = "contact:sends";

const recentSends = () => {
  try {
    const hourAgo = Date.now() - 60 * 60 * 1000;
    return JSON.parse(localStorage.getItem(STORE_KEY) || "[]").filter(
      (t) => t > hourAgo,
    );
  } catch {
    return [];
  }
};

const MESSAGES = {
  wait: "Please wait a moment before sending another message.",
  limit: `That is enough messages for now. Please email ${profile.email} directly.`,
  sent: "Thanks, your message was sent. I’ll reply by email.",
  error: `Sending failed. Please email ${profile.email} directly.`,
};

const Content = () => {
  const formRef = useRef(null);
  const [state, setState] = useState("idle"); // idle | sending | sent | error
  const [copied, setCopied] = useState(false);
  const [done, setDone] = useState({
    name: false,
    email: false,
    project: false,
  });
  const [chars, setChars] = useState(0);
  const openedAt = useRef(Date.now());

  // Tracks which fields are complete (drives the progress dots) and the message length.
  const onInput = (e) => {
    const el = e.target;
    if (!(el.name in done)) return;
    setDone((d) => ({
      ...d,
      [el.name]: el.value.trim() !== "" && el.checkValidity(),
    }));
    if (el.name === "project") setChars(el.value.length);
    if (["sent", "error", "wait", "limit"].includes(state)) setState("idle");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    const data = new FormData(formRef.current);

    // honeypot (hidden from people) and implausibly fast fills are treated as bots: pretend success, send nothing
    if (data.get("website") || Date.now() - openedAt.current < MIN_FILL_MS) {
      formRef.current.reset();
      setState("sent");
      return;
    }
    const sends = recentSends();
    if (sends.length >= MAX_PER_HOUR) return setState("limit");
    if (sends.length && Date.now() - Math.max(...sends) < COOLDOWN_MS)
      return setState("wait");
    setState("sending");
    try {
      await emailjs.send(
        SERVICE,
        TEMPLATE,
        {
          name: data.get("name"),
          email: data.get("email"),
          project: data.get("project"),
          time: new Date().toLocaleString(),
        },
        { publicKey: KEY },
      );
      try {
        localStorage.setItem(
          STORE_KEY,
          JSON.stringify([...recentSends(), Date.now()]),
        );
      } catch {
        /* storage unavailable: the server-side limits still apply */
      }
      formRef.current.reset();
      setDone({ name: false, email: false, project: false });
      setChars(0);
      setState("sent");
    } catch {
      setState("error");
    }
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(profile.email);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable: the address is visible and the mailto link still works */
    }
  };

  return (
    <section
      className="contact section"
      id="contact"
      aria-labelledby="contact-title"
    >
      <div className="container contact__grid">
        <div>
          <header className="section__head">
            <span className="eyebrow">06 — Contact</span>
            <h2 id="contact-title" className="section__title">
              Let’s talk about the work
            </h2>
            <p className="section__lead">
              The fastest way to reach me is email. I’m happy to walk through
              any case file, the trade-offs, or what I am building next.
            </p>
          </header>

          <ul className="contact__links">
            <li>
              <a className="contact__link" href={`mailto:${profile.email}`}>
                <Icon name="mail" /> {profile.email}
              </a>
              <button type="button" className="btn btn--sm" onClick={copyEmail}>
                <Icon name={copied ? "check" : "copy"} />{" "}
                {copied ? "Copied" : "Copy"}
                <span className="sr-only"> email address</span>
              </button>
            </li>
            <li>
              <a
                className="contact__link"
                href={profile.github}
                target="_blank"
                rel="noreferrer"
              >
                <Icon name="github" /> github.com/{profile.handle}
                <span className="sr-only"> (opens in a new tab)</span>
              </a>
            </li>
            <li>
              <a
                className="contact__link"
                href={profile.linkedin}
                target="_blank"
                rel="noreferrer"
              >
                <Icon name="linkedin" /> LinkedIn
                <span className="sr-only"> (opens in a new tab)</span>
              </a>
            </li>
            <li>
              <a
                className="contact__link"
                href={profile.telegram}
                target="_blank"
                rel="noreferrer"
              >
                <BrandIcon name="telegram" /> Telegram
                <span className="sr-only"> (opens in a new tab)</span>
              </a>
            </li>
          </ul>
        </div>

        {FORM_ENABLED && (
          <form
            ref={formRef}
            className="slip"
            data-state={state}
            onSubmit={onSubmit}
            onInput={onInput}
            aria-describedby="form-status"
          >
            <div className="slip__head" aria-hidden="true">
              <span>New message</span>
              <span>to {profile.name.toLowerCase()}</span>
            </div>

            <ol className="slip__steps" aria-hidden="true">
              {Object.entries(done).map(([k, v]) => (
                <li key={k} data-done={v || undefined} />
              ))}
            </ol>

            <input
              className="slip__trap"
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />

            <label className="slip__field">
              <span className="slip__label">
                <i>01</i> Name
              </span>
              <input
                name="name"
                type="text"
                autoComplete="name"
                placeholder="Who is writing?"
                required
              />
            </label>

            <label className="slip__field">
              <span className="slip__label">
                <i>02</i> Email
              </span>
              <input
                name="email"
                type="email"
                autoComplete="email"
                placeholder="Where can I reply?"
                required
              />
            </label>

            <label className="slip__field slip__field--note">
              <span className="slip__label">
                <i>03</i> Message
                <em aria-hidden="true">{chars} / 1000</em>
              </span>
              <textarea
                name="project"
                rows="5"
                maxLength={1000}
                placeholder="What would you like to talk about?"
                required
              />
            </label>

            <button
              type="submit"
              className="slip__send"
              disabled={state === "sending"}
            >
              <span>{state === "sending" ? "Sending…" : "Send message"}</span>
              <Icon name="arrow" />
            </button>

            <p
              id="form-status"
              className="slip__status"
              data-state={state}
              role="status"
            >
              {MESSAGES[state]}
            </p>

            {/* lands on the slip once the message is sent */}
            <svg
              className="slip__stamp"
              viewBox="0 0 120 120"
              aria-hidden="true"
              focusable="false"
            >
              <circle cx="60" cy="60" r="54" className="slip__stamp-ring" />
              <circle
                cx="60"
                cy="60"
                r="46"
                className="slip__stamp-ring slip__stamp-ring--in"
              />
              <text
                x="60"
                y="68"
                textAnchor="middle"
                className="slip__stamp-text"
              >
                SENT
              </text>
            </svg>
          </form>
        )}
      </div>
    </section>
  );
};

export default Content;
